import { inject, Injectable, Signal } from "@angular/core";
import { CommandeApiService } from "./commande-api.service";
import { CommandeState, CommandeStoreService } from "./commande-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import {
  EntityMetadata,
  QueryParam,
} from "../../../../shared/store/store-types.model";
import { Commande } from "../../models/commande.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { CommandeLigne } from "../../models/commande-ligne";
import { CommandeMailPayload } from "../../models/commande-mail-payload.model";
import { firstValueFrom } from "rxjs";
import { CommandeEtat } from "../../enums/etat-commande.enum";
import { Tiers } from "../../../tiers/models/tiers.model";

@Injectable({ providedIn: "root" })
export class CommandeFacadeService extends EntityFacadeService<
  Commande,
  CommandeApiService,
  CommandeState,
  EntityMetadata,
  CommandeStoreService
> {
  constructor() {
    const api = inject(CommandeApiService);
    const store = inject(CommandeStoreService);
    super(api, store);
    store.initStatus(CommandeFacadeService);
  }

  @Untrack
  @UpdateStatus
  async loadById(id: string, query?: QueryParam) {
    const commande = await super.loadById(id, query);
    if (commande) {
      this.store.getWritableState().commande.set({ ...commande() });
    }
  }

  @Untrack
  @UpdateStatus
  async addMulti(commandes: Commande[]) {
    const commandesSaved = await firstValueFrom(this.api.addMulti(commandes));
    this.store.addMany(commandesSaved);
    return commandesSaved;
  }

  @Untrack
  @UpdateStatus
  async add(commande: Commande) {
    const saved = await super.add(commande);
    this.store
      .getWritableState()
      .commande.set({ ...saved(), lignes: [...saved().lignes] });
    return saved;
  }

  @Untrack
  @UpdateStatus
  async update(commande: Commande) {
    const saved = await super.update(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set({ ...saved() });
    }
    return saved;
  }

  @Untrack
  @UpdateStatus
  async sendMail(a: CommandeMailPayload) {
    const commande = await firstValueFrom(this.api.sendMail(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set({ ...commande });
    }
    return commande;
  }

  @Untrack
  @UpdateStatus
  async confirm(c: Commande) {
    const commande = await firstValueFrom(this.api.confirm(c));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(commande);
    }
    return commande;
  }

  async updateDocumentListeId(commandeId: string, documentListId: string) {
    await this.api.updateDocumentListeId(commandeId, documentListId);
  }

  @Untrack
  @UpdateStatus
  async saveLigne(cl: CommandeLigne) {
    const commandeLigne = await firstValueFrom(this.api.saveLigne(cl));
    const commande = this.store.getById(commandeLigne.commandeId)?.();

    const toUpdate = {
      ...commande,
      lignes: commande.lignes?.map((l) =>
        l.id === commandeLigne.id ? commandeLigne : l,
      ),
    };
    if (commande) {
      this.store.updateOne(toUpdate);
    }
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(toUpdate);
    }
  }

  async addLignes(lignes: CommandeLigne[]) {
    const commande = this.store.getState().commande();
    commande.lignes = commande.lignes.concat(lignes);
    this.store.getWritableState().commande.set({ ...commande });
  }

  @Untrack
  @UpdateStatus
  async loadAllFilter(filters: {
    etats: CommandeEtat[];
    etatsReception?: string[];
    search: string;
    sort: string;
    page?: number;
  }) {
    const page = filters.page ?? 0;
    if (page === 0) {
      this.store.removeAll();
    }
    const commandes = await firstValueFrom(this.api.loadAllFilter(filters));
    this.store.addMany(commandes);
    return commandes;
  }

  @Untrack
  @UpdateStatus
  async solder(a: Commande) {
    const commande = await firstValueFrom(this.api.solder(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async desolder(a: Commande) {
    const commande = await firstValueFrom(this.api.desolder(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async terminer(a: Commande) {
    const commande = await firstValueFrom(this.api.terminer(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async reopen(a: Commande) {
    const commande = await firstValueFrom(this.api.reopen(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async cancel(a: Commande) {
    const commande = await firstValueFrom(this.api.cancel(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async uncancel(a: Commande) {
    const commande = await firstValueFrom(this.api.uncancel(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commande()?.id === commande.id) {
      this.store.getWritableState().commande.set(commande);
    }
  }

  updateTiers(tiers: Tiers) {
    this.elements().forEach((element) => {
      const commande = element();
      if (commande.tiersId === tiers.id) {
        this.store.upsertOne({ ...commande, tiers });
      }
    });
    if (this.store.getState().commande()?.tiersId === tiers.id) {
      this.store.getWritableState().commande.update((c) => {
        return { ...c, tiers };
      });
    }
  }
}
